import { Endpoint, Resource } from '@rabbiagency/rabbi-resources';
import { apiRequest, apiResponse, apiResponseBody } from 'common/resources/interceptors';
import UrlHelper from 'common/utils/url-helper';
import { PARAMS } from 'common/constants';
import { attachJwtToken } from './interceptors';

const { STORY_ID, ORGANIZATION_ID, DOMAIN_ID, TEMPLATE_ID, TEAM_ID, MEMBER_ID, CARD_ID } = PARAMS;

const backendResource = new Resource(UrlHelper.adminBackend, {
	headers: { Accept: 'application/json' },
	requestInterceptors: [apiRequest, attachJwtToken],
	responseInterceptors: [apiResponse, apiResponseBody],
});

const login = new Endpoint(backendResource, '/auth/login');
const verify = new Endpoint(backendResource, '/auth/me', { type: 'GET' });
const logout = new Endpoint(backendResource, '/logout');

// story:
const getStory = new Endpoint(backendResource, `/story/:${STORY_ID}`, { type: 'GET', storyAdapter: true });
const setStoryEditor = new Endpoint(backendResource, `/story/:${STORY_ID}/editor`, { type: 'POST' });
const deleteStoryEditor = new Endpoint(backendResource, `/story/:${STORY_ID}/editor/:${MEMBER_ID}`, { type: 'DELETE' });
const createStory = new Endpoint(backendResource, '/story');
const duplicateStory = new Endpoint(backendResource, `/story/:${STORY_ID}/duplicate`, { type: 'POST' });
const deleteStory = new Endpoint(backendResource, `/story/:${STORY_ID}`, { type: 'DELETE' });
const updateStory = new Endpoint(backendResource, `/story/:${STORY_ID}`, { type: 'PUT' });
const updateLatestStory = new Endpoint(backendResource, `/story/:${STORY_ID}/latest`, { type: 'PUT' });
const updateStoryUrl = new Endpoint(backendResource, `/story/:${STORY_ID}/client-story-id`, { type: 'PATCH' });
const updatePublishedStory = new Endpoint(backendResource, `/story/:${STORY_ID}/published`, { type: 'PUT' });
const unpublishPublishedStory = new Endpoint(backendResource, `/story/:${STORY_ID}/unpublish`, { type: 'POST' });
const postGallery = new Endpoint(backendResource, `/story/:${STORY_ID}/gallery`, { type: 'POST' });
// await getStoryAudience.params({ storyId }).send({ skip: 0, limit: 10 });
const getStoryAudience = new Endpoint(backendResource, `/story/:${STORY_ID}/audience`, {
	type: 'POST',
});
const getReportStoryBigPicture = new Endpoint(backendResource, `/story/:${STORY_ID}/analytics/big-picture`, {
	type: 'GET',
});
const getReportStoryByCard = new Endpoint(backendResource, `/story/:${STORY_ID}/analytics/card-by-card`, {
	type: 'GET',
});
const getCsvUrlStoryByCard = new Endpoint(backendResource, `/story/:${STORY_ID}/analytics/card-by-card`, {
	type: 'POST',
});
const getCardAudience = new Endpoint(backendResource, `/story/:${STORY_ID}/audience/card/:${CARD_ID}`, {
	type: 'GET',
});
const storyRollback = new Endpoint(backendResource, `/story/:${STORY_ID}/rollback`, { type: 'POST' });

const setDefaultStoryDomain = new Endpoint(backendResource, `/story/:${STORY_ID}/domain`, {
	type: 'PATCH',
});

// member:
const updateMemberEmail = new Endpoint(backendResource, `/member/:${MEMBER_ID}/email`, { type: 'PATCH' });
const getMember = new Endpoint(backendResource, `/member/:${MEMBER_ID}`, { type: 'GET' });
const resetPassword = new Endpoint(backendResource, `/member/password/reset`, { type: 'POST' });
const restorePassword = new Endpoint(backendResource, `/member/password/restore`, { type: 'POST' });
const getMemberPreferences = new Endpoint(backendResource, `/member/:${MEMBER_ID}/preferences`, { type: 'GET' });
const setMemberPreferences = new Endpoint(backendResource, `/member/:${MEMBER_ID}/preferences`, { type: 'POST' });
const updateMemberAvatar = new Endpoint(backendResource, `/member/:${MEMBER_ID}/profile-picture`, { type: 'PATCH' });
const updateMemberPassword = new Endpoint(backendResource, `/member/:${MEMBER_ID}/password`, { type: 'PATCH' });
const updateMember = new Endpoint(backendResource, `/member/:${MEMBER_ID}`, { type: 'PUT' });
const verifyMember = new Endpoint(backendResource, `/member/verify`, { type: 'POST' });
const verifyMemberResend = new Endpoint(backendResource, `/member/:${MEMBER_ID}/email/verify`, { type: 'POST' });
const createMember = new Endpoint(backendResource, `/member`, { type: 'POST' });
const deleteMember = new Endpoint(backendResource, `/member/:${MEMBER_ID}`, { type: 'DELETE' });

// team:
const createTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team`, { type: 'POST' });
const getTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}`, { type: 'GET' });
const updateTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}`, { type: 'PUT' });
const deleteTeam = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}`, {
	type: 'DELETE',
});
const getTeamStories = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/story`, {
	type: 'GET',
	storyAdapter: true,
});
const getTeamStoriesOfMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member/:${MEMBER_ID}/story`,
	{
		type: 'GET',
		storyAdapter: true,
	}
);
const getTeamMembers = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member`, {
	type: 'GET',
});
const updateTeamMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member/:${MEMBER_ID}`,
	{ type: 'PUT' }
);
const deleteTeamMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/team/:${TEAM_ID}/member/:${MEMBER_ID}`,
	{ type: 'DELETE' }
);

// payment plans
const getPlans = new Endpoint(backendResource, `/plan`, { type: 'GET' });
const getPlanChargeInfo = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/plan-switch`, {
	type: 'GET',
});

// organization:
const createOrganization = new Endpoint(backendResource, '/organization', { type: 'POST' });
const validateOrganizationName = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}`, { type: 'HEAD' });
const updateOrganizationSettings = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}`, { type: 'PUT' });
const getOrganizationTeams = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/team`, { type: 'GET' });
const getPubNubAuthKey = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/pub-sub-key`, {
	type: 'GET',
});
const addMembersToOrganizationTeams = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/invite`, {
	type: 'POST',
});
const getOrganizationMembers = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/member`, {
	type: 'GET',
});
const getOrganizationGoogleFonts = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/fonts?sort=alpha`, {
	type: 'GET',
});
const getOrganizationAdminInvoices = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/invoice`, {
	type: 'GET',
});
const getOrganizationAdminBilling = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/billing`, {
	type: 'GET',
});
const getOrganizationDomains = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/domain`, {
	type: 'GET',
});
const removeMemberFromOrganization = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}`,
	{ type: 'DELETE' }
);
const removeOrganizationDomain = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/domain/:${DOMAIN_ID}`,
	{ type: 'DELETE' }
);
const retryOrganizationDomainConnection = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/domain/:${DOMAIN_ID}/retry`,
	{ type: 'POST' }
);
const updateOrganizationMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}`,
	{ type: 'PUT' }
);
const updateOrganizationBilling = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/billing`, {
	type: 'PUT',
});
const updateOrganizationPayment = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/payment`, {
	type: 'PUT',
});
const resendInviteToOraganization = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}/invite-resend`,
	{ type: 'POST' }
);
const updateOrganizationDomain = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/domain`, {
	type: 'POST',
});
const setDefaultOrganizationDomain = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/domain`, {
	type: 'PATCH',
});
const getOrganizationDomainStatus = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/domain/:${DOMAIN_ID}`,
	{
		type: 'GET',
	}
);
const getOrganizationStories = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/story`, {
	type: 'GET',
});
const getOrganizationMemberStories = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}/story`,
	{ type: 'GET', storyAdapter: true }
);
const getOrganizationTeamsOfMember = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}`,
	{ type: 'GET' }
);

const acceptInviteToOrganization = new Endpoint(
	backendResource,
	`/organization/:${ORGANIZATION_ID}/member/:${MEMBER_ID}/invite-accept`,
	{ type: 'POST' }
);

const setupInviteToOraganization = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/invite-setup`, {
	type: 'POST',
});
const cancelSubscription = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/cancel-subscription`, {
	type: 'POST',
});
const upgradeSubscription = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/plan`, {
	type: 'PUT',
});
const postOrgGallery = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/asset`, { type: 'POST' });
const supportToken = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/support-token`, { type: 'POST' });

// templates
const getTemplates = new Endpoint(backendResource, '/template', {
	type: 'GET',
});

const getTemplate = new Endpoint(backendResource, `/template/:${TEMPLATE_ID}`, {
	type: 'GET',
});

// analytics:
const getReportStories = new Endpoint(backendResource, `/organization/:${ORGANIZATION_ID}/reports-page`, {
	type: 'GET',
});
const getStoryViewStats = new Endpoint(backendResource, `/analytics/story/:${STORY_ID}/views`, { type: 'GET' });
const getStoryCardsViewStats = new Endpoint(backendResource, `/analytics/story/:${STORY_ID}/card-views`, {
	type: 'GET',
});
const getStoryCardsAnswersStats = new Endpoint(backendResource, `/story/:${STORY_ID}/answer-distribution`, {
	type: 'GET',
});

export {
	login,
	verify,
	logout,
	// story:
	getStory,
	createStory,
	duplicateStory,
	deleteStory,
	updateStory,
	updateLatestStory,
	updatePublishedStory,
	unpublishPublishedStory,
	updateStoryUrl,
	postGallery,
	getReportStoryBigPicture,
	storyRollback,
	getReportStoryByCard,
	getCsvUrlStoryByCard,
	setStoryEditor,
	deleteStoryEditor,
	setDefaultStoryDomain,
	// member:
	getMember,
	resetPassword,
	restorePassword,
	getMemberPreferences,
	setMemberPreferences,
	updateMember,
	updateMemberAvatar,
	updateMemberEmail,
	updateMemberPassword,
	deleteMember,
	createMember,
	verifyMember,
	verifyMemberResend,
	// team:
	createTeam,
	getTeam,
	updateTeam,
	deleteTeam,
	getTeamStories,
	getTeamStoriesOfMember,
	getTeamMembers,
	updateTeamMember,
	deleteTeamMember,
	// organization:
	createOrganization,
	getOrganizationAdminBilling,
	getOrganizationAdminInvoices,
	updateOrganizationBilling,
	updateOrganizationPayment,
	getPubNubAuthKey,
	getOrganizationGoogleFonts,
	getOrganizationMemberStories,
	getOrganizationTeamsOfMember,
	getOrganizationTeams,
	addMembersToOrganizationTeams,
	removeMemberFromOrganization,
	validateOrganizationName,
	getOrganizationMembers,
	updateOrganizationSettings,
	updateOrganizationMember,
	acceptInviteToOrganization,
	setupInviteToOraganization,
	resendInviteToOraganization,
	cancelSubscription,
	upgradeSubscription,
	postOrgGallery,
	supportToken,
	updateOrganizationDomain,
	getOrganizationDomainStatus,
	getOrganizationDomains,
	getOrganizationStories,
	removeOrganizationDomain,
	retryOrganizationDomainConnection,
	setDefaultOrganizationDomain,
	// payment plans
	getPlans,
	getPlanChargeInfo,
	// templates
	getTemplates,
	getTemplate,
	// analytics:
	getReportStories,
	getStoryViewStats,
	getStoryCardsViewStats,
	getStoryCardsAnswersStats,
	getStoryAudience,
	getCardAudience,
};
